import Button from '@/components/ui/Buttons/Button';
import { trackNotifyMeEvent } from '@/analytics/library';
import RichText from '@/components/cms/RichText';
import useNotifyMeSubmit from '@/hooks/PDP/useNotifyMeSubmit';
import { NotifyPanelItems } from '@/redux/slices/pdpSlice';
import TextInput from '@/components/ui/Form/TextInput';
import {
  NOTIFY_PANEL_GUEST_MESSAGE,
  NOTIFY_PANEL_SUCCESS_HEADER,
  NOTIFY_PANEL_SUCCESS_MESSAGE
} from '@/components/ui/PDP/ECommBox/constants';
import { EcommPanelParams } from './hooks/useEcommData';
import { FormEvent, useCallback } from 'react';
import * as Form from '@radix-ui/react-form';
import { getNotifyMeType } from '../PDP/ECommBox/notifyMeHelpers';

type EcommPanelNotifyMeProps = {
  params: EcommPanelParams;
};

const DEFAULT_NOTIFY_HEADER = (
  <>
    Get notified when this program
    <br />
    is available on-demand
  </>
);

const DEFAULT_NOTIFY_BODY = (
  <>
    <p>This program will be available on-demand soon.</p>
    <p className="mt-5">
      If you’d like us to let you know when that version is available, just click below and we’ll
      send an email to the address you use to log in to PLI when it’s ready.
    </p>
  </>
);

const getNonEmptyString = (value: unknown) => {
  if (typeof value !== 'string') return undefined;

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

const buildNotifyItems = (params: EcommPanelParams): NotifyPanelItems => {
  const notifyMeUrl = params.notifyItems?.url ?? params.notifyMeUrl;

  return {
    ...params.notifyItems,
    url: notifyMeUrl ?? undefined,
    buttonLabel: params.notifyItems?.buttonLabel ?? 'Notify me',
    notifyMeType:
      params.notifyItems?.notifyMeType ??
      getNotifyMeType(params.searchableType === 'liveprogram', params.notifyItems?.source),
    source: params.notifyItems?.source
  };
};

const EcommPanelNotifyMe = ({ params }: EcommPanelNotifyMeProps) => {
  const notifyItems = buildNotifyItems(params);
  const {
    email,
    firstName,
    isGuestNativeNotifyMe,
    isLoading,
    isSubmitting,
    lastName,
    setEmail,
    setFirstName,
    setLastName,
    submitError,
    submitted,
    submitNotifyMe
  } = useNotifyMeSubmit({
    notifyItems,
    notifyMeUrl: notifyItems.url ?? params.notifyMeUrl ?? undefined,
    code: params.notifyMeCode ?? params.pk,
    context: params.context,
    onTrackNotifyMe: () => {
      if (params.itemAnalyticsModel && params.context) {
        trackNotifyMeEvent(params.itemAnalyticsModel, params.context);
      }
    }
  });
  const header = submitted
    ? (getNonEmptyString(notifyItems.successHeaderLabel) ?? NOTIFY_PANEL_SUCCESS_HEADER)
    : getNonEmptyString(notifyItems.headerLabel);
  const bodyContent = submitted
    ? (getNonEmptyString(notifyItems.successMessageLabel) ?? NOTIFY_PANEL_SUCCESS_MESSAGE)
    : notifyItems.messageLabel?.toString()?.trim() || undefined;
  const defaultBody = isGuestNativeNotifyMe ? (
    <p>{NOTIFY_PANEL_GUEST_MESSAGE}</p>
  ) : (
    DEFAULT_NOTIFY_BODY
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      await submitNotifyMe(event.currentTarget);
    },
    [submitNotifyMe]
  );

  return (
    <section className="flex min-h-[900px] w-full flex-col border-none bg-white px-10 pb-10 pt-16 text-black">
      <div className="flex w-full flex-col gap-12">
        <div className="flex w-full flex-col gap-4">
          <h2 className="heading-6-medium max-w-[342px] text-black">
            {header ?? DEFAULT_NOTIFY_HEADER}
          </h2>
          <div className="text-1 max-w-[342px] text-charcoal">
            {bodyContent != null ? <RichText content={bodyContent} /> : defaultBody}
          </div>
        </div>
        {!submitted && (
          <Form.Root className="flex max-w-[342px] flex-col gap-4" onSubmit={handleSubmit}>
            {isGuestNativeNotifyMe && (
              <div className="mb-10 flex w-full flex-col gap-14">
                <TextInput
                  label="First name"
                  name="firstName"
                  value={firstName}
                  onChange={setFirstName}
                  inputProps={{ required: true, 'aria-required': true }}
                  maxLength={80}
                />
                <TextInput
                  label="Last name"
                  name="lastName"
                  value={lastName}
                  onChange={setLastName}
                  inputProps={{ required: true, 'aria-required': true }}
                  maxLength={80}
                />
                <TextInput
                  label="Email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  inputProps={{ required: true, 'aria-required': true }}
                  maxLength={80}
                />
              </div>
            )}
            {submitError && (
              <p className="text-2 text-red" role="alert">
                {submitError}
              </p>
            )}
            <Button
              color="red"
              size="large"
              label={notifyItems.buttonLabel}
              type="submit"
              disabled={isLoading || isSubmitting}
              loading={isLoading || isSubmitting}
              className="w-full !justify-center"
            />
          </Form.Root>
        )}
      </div>
    </section>
  );
};

export default EcommPanelNotifyMe;
